import { useState } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { MailCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import type { ApiError } from "@/lib/api/client";
import { useExpertSignup } from "@/features/expert/api/hooks";
import "@/features/expert/i18n";

import { AuthField, PasswordInput, TextInput } from "./auth-fields";
import { AuthScreen } from "./auth-screen";

/**
 * Public expert account creation (`/experts/signup`). Creates the account via
 * the expert signup endpoint (claiming an invitation when a `token` is present,
 * marking the role Expert). Verification is mandatory (the API blocks login
 * until the emailed link is clicked — Option A / A-4), so we do NOT auto-log-in:
 * on success we show a "check your email" screen. Once verified the expert signs
 * in and lands in the wizard; its email-confirmation gate (H-9) remains a
 * backstop for a mid-registration re-entry.
 */
export function ExpertSignupPage() {
  const { t } = useTranslation();
  const signup = useExpertSignup();
  const search = useSearch({ from: "/experts/signup" });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(search.email ?? "");
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (password1 !== password2) {
      setError(t("auth.signup.passwordMismatch"));
      return;
    }
    try {
      await signup.mutateAsync({
        email: email.trim(),
        first_name: firstName,
        last_name: lastName,
        password1,
        password2,
        invitation_token: search.token,
      });
      setSentTo(email.trim());
    } catch (err) {
      const apiErr = err as ApiError & { detail?: string };
      const fieldMsg = apiErr?.fieldErrors
        ? Object.values(apiErr.fieldErrors).flat()[0]
        : undefined;
      setError(fieldMsg ?? apiErr?.detail ?? t("auth.genericError"));
    }
  };

  if (sentTo) {
    return (
      <AuthScreen>
        <div className="space-y-4">
          <MailCheck className="size-8 text-primary" aria-hidden />
          <h1 className="text-xl font-semibold">{t("auth.signup.sentTitle")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("auth.signup.sentBody", { email: sentTo })}
          </p>
          <Button asChild className="mt-2">
            <Link to="/login">{t("auth.signup.backToLogin")}</Link>
          </Button>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <div className="space-y-2">
        <h1 className="text-xl font-semibold">{t("expert.signup.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("expert.signup.subtitle")}
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <AuthField label={t("expert.register.fields.contact_primary_first_name")} htmlFor="first_name">
          <TextInput id="first_name" value={firstName} onChange={setFirstName} autoComplete="given-name" />
        </AuthField>
        <AuthField label={t("expert.register.fields.contact_primary_last_name")} htmlFor="last_name">
          <TextInput id="last_name" value={lastName} onChange={setLastName} autoComplete="family-name" />
        </AuthField>
        <AuthField label={t("expert.register.fields.email")} htmlFor="email">
          <TextInput id="email" type="email" value={email} onChange={setEmail} autoComplete="email" />
        </AuthField>
        <AuthField label={t("expert.signup.password")} htmlFor="password1">
          <PasswordInput id="password1" value={password1} onChange={setPassword1} autoComplete="new-password" />
        </AuthField>
        <AuthField
          label={t("expert.signup.passwordConfirm")}
          htmlFor="password2"
          error={error ?? undefined}
        >
          <PasswordInput id="password2" value={password2} onChange={setPassword2} autoComplete="new-password" />
        </AuthField>

        <Button type="submit" className="mt-6 w-full" disabled={signup.isPending}>
          {t("expert.signup.submit")}
        </Button>
      </form>
      <p className="mt-4 text-center text-sm text-muted-foreground">
        <Link to="/login" className="font-semibold text-foreground hover:underline">
          {t("expert.invite.toLogin")}
        </Link>
      </p>
    </AuthScreen>
  );
}
